import { useState, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../../api/client';
import { useCan } from '../../../lib/rbac';
import {
  Search, Loader2, ShoppingCart, Check,
  AlertCircle, Package, Key, Flame,
} from 'lucide-react';

interface KeepaSearchBarProps {
  sellerId: number;
  trendingKeywords?: string[];
  sites?: string[];
  filters?: any;
}

interface KeepaResult {
  asin: string;
  title: string;
  price: number | null;
  monthly_sold: number | null;
  rating: number | null;
  main_image: string | null;
  is_available: boolean;
  status: 'new' | 'in_queue' | 'in_drafts' | 'published';
  is_duplicate: boolean;
}

const STATUS_CONFIG: Record<string, { label: string; color: string; bg: string }> = {
  new: { label: 'tendencias.statusNew', color: 'text-green-700 dark:text-green-300', bg: 'bg-green-100 dark:bg-green-900/30' },
  in_queue: { label: 'tendencias.statusInQueue', color: 'text-blue-700 dark:text-blue-300', bg: 'bg-blue-100 dark:bg-blue-900/30' },
  in_drafts: { label: 'tendencias.statusDraft', color: 'text-yellow-700 dark:text-yellow-300', bg: 'bg-yellow-100 dark:bg-yellow-900/30' },
  published: { label: 'tendencias.statusPublished', color: 'text-purple-700 dark:text-purple-300', bg: 'bg-purple-100 dark:bg-purple-900/30' },
};

// Apply feeder filters client-side to Keepa search results
function applyFeederFilters(items: KeepaResult[], filters: any): KeepaResult[] {
  if (!filters) return items;
  const en = filters._enabled || {};
  const ck = (k: string) => en[k] !== false;
  return items.filter(item => {
    // Price range (Keepa prices are in USD cents in filters, but item.price is already dollars)
    if (ck('current_NEW_gte') && filters.current_NEW_gte && item.price != null) {
      if (item.price * 100 < parseInt(filters.current_NEW_gte)) return false;
    }
    if (ck('current_NEW_lte') && filters.current_NEW_lte && item.price != null) {
      if (item.price * 100 > parseInt(filters.current_NEW_lte)) return false;
    }
    // Monthly sold minimum
    if (ck('monthlySold_gte') && filters.monthlySold_gte && item.monthly_sold != null) {
      if (item.monthly_sold < parseInt(filters.monthlySold_gte)) return false;
    }
    // Min rating
    if (ck('minRating') && filters.minRating && item.rating != null) {
      if (item.rating * 10 < parseInt(filters.minRating)) return false;
    }
    return true;
  });
}

export default function KeepaSearchBar({ sellerId, trendingKeywords, sites, filters }: KeepaSearchBarProps) {
  const { t } = useTranslation();
  const canMoney = useCan()('global.view_money');
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<KeepaResult[]>([]);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(false);
  const [queueing, setQueueing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [queueSuccess, setQueueSuccess] = useState<number | null>(null);
  const [tokensLeft, setTokensLeft] = useState<number | null>(null);
  const [overrideCategory, setOverrideCategory] = useState<{ categoryId: string; categoryName: string } | null>(null);
  const [searchedKeyword, setSearchedKeyword] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const lastSearchTime = useRef(0);

  const doSearch = useCallback(async (keyword: string, page: number = 0) => {
    const trimmed = keyword.trim();
    if (trimmed.length < 2) return;
    const now = Date.now();
    if (now - lastSearchTime.current < 2000) return;
    lastSearchTime.current = now;
    const isLoadMore = page > 0;
    if (isLoadMore) {
      setLoadingMore(true);
    } else {
      setLoading(true);
      setResults([]);
      setSelected(new Set());
      setQueueSuccess(null);
      setCurrentPage(0);
      setHasMore(false);
    }
    setError(null);
    setSearchedKeyword(trimmed);
    try {
      const res = await api.post<any>('/publisher/discover/tendencias/search-keepa', {
        keyword: trimmed,
        seller_id: sellerId,
        page,
      });
      if (res.success && res.data) {
        const rawItems = res.data.items || [];
        const items = applyFeederFilters(rawItems, filters);
        if (isLoadMore) {
          // Append new results, dedup by asin
          setResults(prev => {
            const existingAsins = new Set(prev.map(r => r.asin));
            const newItems = items.filter((r: KeepaResult) => !existingAsins.has(r.asin));
            return [...prev, ...newItems];
          });
          // Auto-select new items
          setSelected(prev => {
            const next = new Set(prev);
            for (const r of items) {
              if (!r.is_duplicate) next.add(r.asin);
            }
            return next;
          });
        } else {
          setResults(items);
          const autoSelect = new Set<string>();
          for (const r of items) {
            if (!r.is_duplicate) autoSelect.add(r.asin);
          }
          setSelected(autoSelect);
        }
        if (res.data.tokens_left != null) setTokensLeft(res.data.tokens_left);
        setOverrideCategory(res.data.override_category || null);
        setCurrentPage(page);
        setHasMore(res.data.has_more || false);
      } else {
        setError(res.error === 'keepa_rate_limit' ? t('tendencias.keepaQuota') : (res.error || 'Search failed'));
      }
    } catch (err) {
      console.error('Keepa search failed', err);
      setError('Network error');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [sellerId, filters]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    doSearch(query);
  };

  const handlePillClick = (keyword: string) => {
    setQuery(keyword);
    doSearch(keyword);
  };

  const toggleSelect = (asin: string) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(asin)) next.delete(asin);
      else next.add(asin);
      return next;
    });
  };

  const toggleAll = () => {
    if (selected.size > 0) {
      setSelected(new Set());
    } else {
      const selectable = results.filter(r => r.status === 'new');
      setSelected(new Set(selectable.map(r => r.asin)));
    }
  };

  const handleQueue = async () => {
    if (selected.size === 0) return;
    setQueueing(true);
    setError(null);
    try {
      const res = await api.post<any>('/publisher/discover/tendencias/queue', {
        asins: Array.from(selected),
        seller_id: sellerId,
        sites: sites || [],
        override_category_id: overrideCategory?.categoryId || undefined,
        override_category_name: overrideCategory?.categoryName || undefined,
      });
      if (res.success && res.data) {
        setQueueSuccess(res.data.queued || selected.size);
        setResults(prev =>
          prev.map(r => selected.has(r.asin) ? { ...r, status: 'in_queue' as const, is_duplicate: true } : r)
        );
        setSelected(new Set());
      } else {
        setError(res.error || 'Queue failed');
      }
    } catch (err) {
      console.error('Queue failed', err);
      setError('Network error');
    } finally {
      setQueueing(false);
    }
  };

  const selectableCount = results.filter(r => r.status === 'new').length;

  return (
    <div
      className="rounded-lg border p-4"
      style={{ borderColor: 'var(--color-border)', backgroundColor: 'var(--color-bg-secondary)' }}
    >
      <form onSubmit={handleSubmit} className="flex gap-2 mb-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: 'var(--color-text-tertiary)' }} />
          <input
            type="text"
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder={t('tendencias.keepaSearchPlaceholder')}
            className="w-full pl-9 pr-3 py-2 text-sm rounded-md border"
            style={{
              borderColor: 'var(--color-border)',
              backgroundColor: 'var(--color-bg)',
              color: 'var(--color-text)',
            }}
          />
        </div>
        <button
          type="submit"
          disabled={loading || query.trim().length < 2}
          className="px-4 py-2 rounded-md text-sm font-medium text-white disabled:opacity-40 transition-opacity"
          style={{ backgroundColor: 'var(--color-primary)' }}
        >
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : t('tendencias.keepaSearchBtn')}
        </button>
      </form>

      {trendingKeywords && trendingKeywords.length > 0 && !results.length && !loading && (
        <div className="mb-3">
          <p className="text-xs mb-2 flex items-center gap-1" style={{ color: 'var(--color-text-tertiary)' }}>
            <Flame className="w-3 h-3" />
            {t('tendencias.keepaSuggestions')}
          </p>
          <div className="flex flex-wrap gap-1.5">
            {trendingKeywords.slice(0, 15).map((kw, idx) => (
              <button
                key={`${kw}-${idx}`}
                onClick={() => handlePillClick(kw)}
                className="px-2.5 py-1 rounded-full text-xs font-medium hover:opacity-80 transition-opacity"
                style={{
                  backgroundColor: 'var(--color-bg-tertiary)',
                  color: 'var(--color-text-secondary)',
                }}
              >
                {kw}
              </button>
            ))}
          </div>
        </div>
      )}

      {(tokensLeft != null || overrideCategory) && (
        <div className="flex items-center gap-3 mb-2 text-xs" style={{ color: 'var(--color-text-tertiary)' }}>
          {tokensLeft != null && (
            <span className="flex items-center gap-1">
              <Key className="w-3 h-3" />
              Keepa: {tokensLeft.toLocaleString()} tokens
            </span>
          )}
          {overrideCategory && (
            <span className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 font-medium">
              CBT: {overrideCategory.categoryName}
            </span>
          )}
        </div>
      )}

      {loading && (
        <div className="flex flex-col items-center justify-center py-8 gap-2">
          <Loader2 className="w-6 h-6 animate-spin" style={{ color: 'var(--color-primary)' }} />
          <p className="text-xs" style={{ color: 'var(--color-text-tertiary)' }}>
            {t('tendencias.searchingKeepa')}
          </p>
        </div>
      )}

      {error && (
        <div className="flex items-center gap-2 py-4 justify-center">
          <AlertCircle className="w-4 h-4 text-red-500" />
          <p className="text-xs text-red-500">{error}</p>
        </div>
      )}

      {!loading && !error && searchedKeyword && results.length === 0 && (
        <div className="flex flex-col items-center justify-center py-6 gap-2">
          <Package className="w-5 h-5" style={{ color: 'var(--color-text-tertiary)' }} />
          <p className="text-xs" style={{ color: 'var(--color-text-tertiary)' }}>
            {t('tendencias.noResults')} — "{searchedKeyword}"
          </p>
        </div>
      )}

      {results.length > 0 && (
        <>
          <div className="overflow-x-auto" style={{ maxHeight: '500px', overflowY: 'auto' }}>
            <table className="w-full text-xs">
              <thead className="sticky top-0 z-10" style={{ backgroundColor: 'var(--color-bg-secondary)' }}>
                <tr className="border-b" style={{ borderColor: 'var(--color-border)' }}>
                  <th className="px-3 py-2 text-left w-8">
                    <input
                      type="checkbox"
                      checked={selected.size > 0}
                      onChange={toggleAll}
                      className="rounded"
                    />
                  </th>
                  <th className="px-2 py-2 text-left w-12" />
                  <th className="px-2 py-2 text-left" style={{ color: 'var(--color-text-tertiary)' }}>
                    {t('tendencias.colProduct')}
                  </th>
                  <th className="px-2 py-2 text-right w-20" style={{ color: 'var(--color-text-tertiary)' }}>
                    {t('tendencias.colPrice')}
                  </th>
                  <th className="px-2 py-2 text-center w-16" style={{ color: 'var(--color-text-tertiary)' }}>
                    {t('tendencias.colRating')}
                  </th>
                  <th className="px-2 py-2 text-right w-20" style={{ color: 'var(--color-text-tertiary)' }}>
                    {t('tendencias.colMonthlySold')}
                  </th>
                  <th className="px-2 py-2 text-center w-24" style={{ color: 'var(--color-text-tertiary)' }}>
                    {t('tendencias.colStatus')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {results.map(item => {
                  const statusCfg = STATUS_CONFIG[item.status] || STATUS_CONFIG.new;
                  const isSelectable = item.status === 'new';
                  return (
                    <tr
                      key={item.asin}
                      className="border-b hover:opacity-90 transition-opacity"
                      style={{ borderColor: 'var(--color-border)' }}
                    >
                      <td className="px-3 py-2">
                        <input
                          type="checkbox"
                          checked={selected.has(item.asin)}
                          onChange={() => toggleSelect(item.asin)}
                          disabled={!isSelectable}
                          className="rounded disabled:opacity-30"
                        />
                      </td>
                      <td className="px-2 py-2">
                        {item.main_image ? (
                          <img src={item.main_image} alt="" className="w-10 h-10 object-contain rounded" loading="lazy" />
                        ) : (
                          <div
                            className="w-10 h-10 rounded flex items-center justify-center"
                            style={{ backgroundColor: 'var(--color-bg-tertiary)' }}
                          >
                            <ShoppingCart className="w-4 h-4" style={{ color: 'var(--color-text-tertiary)' }} />
                          </div>
                        )}
                      </td>
                      <td className="px-2 py-2">
                        <div className="max-w-[300px]">
                          <p className="font-medium truncate" style={{ color: 'var(--color-text)' }} title={item.title}>
                            {item.title}
                          </p>
                          <p className="text-[10px] mt-0.5" style={{ color: 'var(--color-text-tertiary)' }}>
                            {item.asin}
                          </p>
                        </div>
                      </td>
                      <td className="px-2 py-2 text-right" style={{ color: 'var(--color-text)' }}>
                        {canMoney && item.price != null ? `$${item.price.toFixed(2)}` : (
                          <span style={{ color: 'var(--color-text-tertiary)' }}>-</span>
                        )}
                      </td>
                      <td className="px-2 py-2 text-center" style={{ color: 'var(--color-text)' }}>
                        {item.rating != null ? item.rating.toFixed(1) : '-'}
                      </td>
                      <td className="px-2 py-2 text-right" style={{ color: 'var(--color-text)' }}>
                        {item.monthly_sold != null ? item.monthly_sold.toLocaleString() : '-'}
                      </td>
                      <td className="px-2 py-2 text-center">
                        <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-medium ${statusCfg.bg} ${statusCfg.color}`}>
                          {t(statusCfg.label)}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="flex items-center justify-between pt-3 mt-2 border-t" style={{ borderColor: 'var(--color-border)' }}>
            <div className="text-xs" style={{ color: 'var(--color-text-tertiary)' }}>
              {results.length} {t('tendencias.results')}
              {selected.size > 0 && ` · ${selected.size} ${t('tendencias.selected')}`}
              {currentPage > 0 && ` · p.${currentPage + 1}`}
            </div>
            <div className="flex items-center gap-2">
              {queueSuccess != null && (
                <span className="flex items-center gap-1 text-xs text-green-600 dark:text-green-400">
                  <Check className="w-3.5 h-3.5" />
                  {queueSuccess} {t('tendencias.queued')}
                </span>
              )}
              {hasMore && (
                <button
                  onClick={() => searchedKeyword && doSearch(searchedKeyword, currentPage + 1)}
                  disabled={loadingMore}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium border transition-opacity disabled:opacity-40"
                  style={{ borderColor: 'var(--color-border)', color: 'var(--color-text-secondary)' }}
                >
                  {loadingMore ? (
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  ) : (
                    <Package className="w-3.5 h-3.5" />
                  )}
                  {t('tendencias.loadMore')}
                </button>
              )}
              <button
                onClick={handleQueue}
                disabled={selected.size === 0 || queueing}
                className="flex items-center gap-1.5 px-4 py-1.5 rounded-md text-xs font-medium text-white disabled:opacity-40 transition-opacity"
                style={{ backgroundColor: 'var(--color-primary)' }}
              >
                {queueing ? (
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                ) : (
                  <ShoppingCart className="w-3.5 h-3.5" />
                )}
                {t('tendencias.enqueue')} ({selected.size})
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
